import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/**
 * Pure helpers for audit trail viewer: date/string formatting, search, and normalization.
 * Entry shape includes user, created timestamps, breadcrumbs, content path, and value metadata.
 */

/**
 * Parse a value to a Date or return null if invalid/missing.
 * @param {string|number|Date|null|undefined} d - Value to parse
 * @returns {Date|null}
 */
export function safeDate(d) {
  if (d == null) return null;
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/**
 * Return the last segment of a breadcrumb string (e.g. "A > B > C" -> "C").
 * @param {string} bc - Breadcrumb string
 * @returns {string}
 */
export function lastBreadcrumbSegment(bc) {
  var _parts;
  const parts = String(bc !== null && bc !== void 0 ? bc : '').split('>');
  const last = ((_parts = parts[parts.length - 1]) !== null && _parts !== void 0 ? _parts : '').trim();
  return last || String(bc !== null && bc !== void 0 ? bc : '').trim() || 'Unknown';
}

/**
 * Format a date for day grouping (e.g. "Wed, Feb 26, 2026").
 * @param {Date} d - Date
 * @returns {string}
 */
export function formatDay(d) {
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  });
}

/**
 * Format time only (e.g. "6:34 PM").
 * @param {Date} d - Date
 * @returns {string}
 */
export function formatTime(d) {
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Case-insensitive includes.
 * @param {string} hay - Haystack
 * @param {string} needle - Needle
 * @returns {boolean}
 */
export function includesCI(hay, needle) {
  return String(hay).toLowerCase().includes(String(needle).toLowerCase());
}
export function displayValueOrDash(value) {
  return value != null && String(value).trim() ? value : '—';
}

/**
 * Build Google Maps search URL for lat/lng.
 * @param {string|number} lat - Latitude
 * @param {string|number} lng - Longitude
 * @returns {string}
 */
export function googleMapsUrl(lat, lng) {
  const q = "".concat(lat, ",").concat(lng);
  return "https://www.google.com/maps/search/?api=1&query=".concat(encodeURIComponent(q));
}

/**
 * True if the value looks like an object with a timestamp _created.
 * The timestamp can be a number or parseable date string.
 * @param {*} obj - value object from an audit entry
 * @returns {boolean}
 */
export function hasTimestampCreated(obj) {
  if (obj == null || typeof obj !== 'object') return false;
  const c = obj._created;
  if (c == null) return false;
  if (typeof c === 'number' && !Number.isNaN(c)) return true;
  if (typeof c === 'string') {
    const d = new Date(c);
    return !Number.isNaN(d.getTime());
  }
  return false;
}

/**
 * Coerce raw value to a display label and optional raw for modal.
 * Objects with _download show as "File Path".
 * Objects with _created timestamp show as "New Item"; other objects show as "Complex value".
 * @param {*} v - displayValue or value from an audit entry
 * @returns {{ displayValue: string|number|undefined, rawValueForModal?: object|Array<*> }}
 */
export function toDisplayValueAndRaw(v) {
  if (v == null) return {
    displayValue: undefined
  };
  if (typeof v === 'string' || typeof v === 'number') return {
    displayValue: v
  };
  if (typeof v === 'object') {
    if (Object.prototype.hasOwnProperty.call(v, '_download')) {
      return {
        displayValue: 'File Path'
      };
    }
    if (hasTimestampCreated(v)) {
      return {
        displayValue: 'New Item',
        rawValueForModal: v
      };
    }
    return {
      displayValue: 'Complex value',
      rawValueForModal: v
    };
  }
  return {
    displayValue: String(v)
  };
}

/**
 * @deprecated Use toDisplayValueAndRaw. Coerce for backward compatibility in tests.
 */
export function toRenderableDisplayValue(v) {
  const _toDisplayValueAndRaw = toDisplayValueAndRaw(v),
    displayValue = _toDisplayValueAndRaw.displayValue;
  return displayValue;
}

/**
 * Normalize raw auditTrailContent (object keyed by UUID) into a sorted list of UI entries.
 * Sorted by _created descending.
 * @param {Object} auditTrailContent - Record of entry ids to audit entries.
 * @returns {Array<Object>} Normalized entries for display.
 */
export function normalizeAuditTrailEntries(auditTrailContent) {
  if (auditTrailContent == null || typeof auditTrailContent !== 'object' || Array.isArray(auditTrailContent)) {
    return [];
  }
  const out = Object.entries(auditTrailContent).filter(_ref => {
    let _ref2 = _slicedToArray(_ref, 2),
      entry = _ref2[1];
    return entry != null && typeof entry === 'object' && !Array.isArray(entry);
  }).map(_ref3 => {
    var _e$breadcrumbs, _e$displayValue, _e$user, _e$contentPath;
    let _ref4 = _slicedToArray(_ref3, 2),
      id = _ref4[0],
      e = _ref4[1];
    const created = e._created;
    const breadcrumbs = ((_e$breadcrumbs = e.breadcrumbs) !== null && _e$breadcrumbs !== void 0 ? _e$breadcrumbs : 'Unknown').trim();
    const _toDisplayValueAndRaw2 = toDisplayValueAndRaw((_e$displayValue = e.displayValue) !== null && _e$displayValue !== void 0 ? _e$displayValue : e.value),
      displayValue = _toDisplayValueAndRaw2.displayValue,
      rawValueForModal = _toDisplayValueAndRaw2.rawValueForModal;
    return {
      id,
      user: ((_e$user = e.user) !== null && _e$user !== void 0 ? _e$user : 'Unknown').trim(),
      created: safeDate(created),
      serverTimestamp: safeDate(e.serverTimestamp),
      breadcrumbs,
      field: lastBreadcrumbSegment(breadcrumbs),
      contentPath: ((_e$contentPath = e.contentPath) !== null && _e$contentPath !== void 0 ? _e$contentPath : '').trim(),
      displayValue,
      rawValueForModal,
      latitude: e.latitude,
      longitude: e.longitude
    };
  });
  out.sort((a, b) => {
    const at = a.created ? a.created.getTime() : -Infinity;
    const bt = b.created ? b.created.getTime() : -Infinity;
    return bt - at;
  });
  const previousDisplayValueByPath = new Map();
  for (let i = out.length - 1; i >= 0; i -= 1) {
    const curr = out[i];
    curr.fromDisplayValue = previousDisplayValueByPath.get(curr.contentPath);
    previousDisplayValueByPath.set(curr.contentPath, curr.displayValue);
  }
  return out;
}
export function getUniqueSortedValues(entries, key) {
  const set = new Set(entries.map(entry => entry[key]).filter(Boolean));
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}
export function getDatePresetMinTime(datePreset) {
  let now = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : new Date();
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  if (datePreset === '24h') return now.getTime() - 24 * 60 * 60 * 1000;
  if (datePreset === '7d') return now.getTime() - 7 * 24 * 60 * 60 * 1000;
  if (datePreset === 'today') return startOfToday.getTime();
  return null;
}
export function filterAuditTrailEntries(entries) {
  let _ref5 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref5$query = _ref5.query,
    query = _ref5$query === void 0 ? '' : _ref5$query,
    _ref5$userFilter = _ref5.userFilter,
    userFilter = _ref5$userFilter === void 0 ? '' : _ref5$userFilter,
    _ref5$fieldFilter = _ref5.fieldFilter,
    fieldFilter = _ref5$fieldFilter === void 0 ? '' : _ref5$fieldFilter,
    _ref5$datePreset = _ref5.datePreset,
    datePreset = _ref5$datePreset === void 0 ? '' : _ref5$datePreset;
  const minTime = getDatePresetMinTime(datePreset);
  const trimmedQuery = query.trim();
  return entries.filter(entry => {
    var _entry$displayValue;
    if (userFilter && entry.user !== userFilter) return false;
    if (fieldFilter && entry.breadcrumbs !== fieldFilter) return false;
    if (minTime != null) {
      const createdTime = entry.created ? entry.created.getTime() : null;
      if (createdTime == null || createdTime < minTime) return false;
    }
    if (!trimmedQuery) return true;
    const haystack = [entry.user, entry.field, entry.breadcrumbs, entry.contentPath, (_entry$displayValue = entry.displayValue) !== null && _entry$displayValue !== void 0 ? _entry$displayValue : ''].join(' • ');
    return includesCI(haystack, trimmedQuery);
  });
}
const groupEntriesBy = (entries, getKey) => Array.from(entries.reduce((map, entry) => {
  const key = getKey(entry);
  if (!map.has(key)) {
    map.set(key, []);
  }
  map.get(key).push(entry);
  return map;
}, new Map()).entries());
export function groupEntriesByDay(entries) {
  return groupEntriesBy(entries, entry => entry.created ? formatDay(entry.created) : 'Unknown date');
}
export function groupEntriesByField(entries) {
  return groupEntriesBy(entries, entry => entry.breadcrumbs || 'Unknown').sort((a, b) => a[0].localeCompare(b[0]));
}
export function stringifyRawValueForModal(raw) {
  try {
    return JSON.stringify(raw, null, 2);
  } catch (_unused) {
    return String(raw);
  }
}